/**
 * ImportDiffDialog — read-only side-by-side preview of one PitchBook
 * company/investor (+ its people) against the CRM.
 *
 * It never writes and never calls a commit endpoint: PitchBook records reach
 * the CRM only through List Management's reviewed, Admin-confirmed commit
 * (#767), and that source needs PitchBook licensing approval (#777). The
 * footer points there.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
    X, Loader2, Building2, User, AlertTriangle,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
    DiffTable, MovedToListManagement, PersonRow, VerdictBadge,
} from './ImportDiffParts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * @param {string}  pitchbookId       PitchBook company/investor ID to preview
 * @param {string}  [recordName]      Display name shown in the header
 * @param {string}  [personFocusId]   Pre-expand and highlight one person
 * @param {string}  [targetAccountId] When set, compare against this CRM
 *                                    account (bypasses the match ladder). Used
 *                                    by the Company detail page's PitchBook tab.
 * @param {boolean} [companyOnly]     When true, hide the People section.
 * @param {function} onClose
 */
export default function ImportDiffDialog({
    pitchbookId,
    recordName,
    personFocusId = null,
    targetAccountId = null,
    companyOnly = false,
    onClose,
}) {
    const { getAuthHeader } = useAuth();
    const [loading, setLoading] = useState(true);
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState(null);
    const [expandedPersons, setExpandedPersons] = useState({});

    const loadPreview = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axios.post(`${API}/pitchbook/manual-sync/preview`,
                {
                    pitchbook_id: pitchbookId,
                    ...(targetAccountId ? { target_account_id: targetAccountId } : {}),
                },
                { headers: getAuthHeader() });
            setPreview(res.data);
            if (personFocusId) setExpandedPersons({ [personFocusId]: true });
        } catch (e) {
            const status = e.response?.status;
            if (status === 422) setError(e.response?.data?.detail || 'This record is too large to preview in one go.');
            else if (status === 404) setError('This record was not found in the indexed PitchBook data.');
            else if (status === 409) setError(e.response?.data?.detail?.drift_report
                ? 'The feed schema changed — resolve the drift before previewing.'
                : (e.response?.data?.detail || 'No indexed PitchBook data yet.'));
            else setError('Failed to load the preview.');
        } finally {
            setLoading(false);
        }
    }, [pitchbookId, personFocusId, targetAccountId, getAuthHeader]);

    useEffect(() => { loadPreview(); }, [loadPreview]);

    const company = preview?.company;
    const isUpdate = company?.verdict === 'update' || company?.verdict === 'skip_no_change';

    return createPortal((
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col"
                 data-testid="pb-preview-dialog">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
                    <div className="min-w-0">
                        <h3 className="font-semibold text-stone-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            PitchBook preview — {recordName || pitchbookId}
                        </h3>
                        <p className="text-xs text-stone-500 mt-0.5">
                            A read-only comparison of the indexed PitchBook data with your CRM.
                            Nothing is written from here.
                        </p>
                    </div>
                    <button onClick={onClose} aria-label="Close"
                        className="text-stone-400 hover:text-stone-700 shrink-0 ml-3"><X className="w-5 h-5" /></button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-5">
                    {loading && (
                        <div className="py-16 text-center text-stone-400">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                            <p className="text-xs">Building the preview…</p>
                        </div>
                    )}

                    {!loading && error && (
                        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                            <p className="text-sm text-amber-800">{error}</p>
                        </div>
                    )}

                    {!loading && !error && preview && (
                        <div className="space-y-5">
                            {/* Company block */}
                            <div>
                                <div className="flex items-center justify-between gap-3 mb-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <Building2 className="w-4 h-4 text-stone-500 shrink-0" />
                                        <h4 className="font-semibold text-stone-800 text-sm truncate">
                                            {company.entity === 'investor' ? 'Investor' : 'Company'}
                                        </h4>
                                        <VerdictBadge verdict={company.verdict} />
                                    </div>
                                    {company.matched_crm_account && (
                                        <span className="text-xs text-stone-500 truncate">→ {company.matched_crm_account.name}</span>
                                    )}
                                </div>
                                <DiffTable fields={company.fields} isUpdate={isUpdate} />
                            </div>

                            {preview.warnings?.length > 0 && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                                    {preview.warnings.map((w, i) => <div key={i}>• {w}</div>)}
                                </div>
                            )}

                            {/* People (hidden in company-only mode) */}
                            {!companyOnly && preview.persons?.length > 0 && (
                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <User className="w-4 h-4 text-stone-500" />
                                        <h4 className="font-semibold text-stone-800 text-sm">
                                            People ({preview.persons.length})
                                        </h4>
                                    </div>
                                    <div className="space-y-2">
                                        {preview.persons.map((p) => (
                                            <PersonRow
                                                key={p.pitchbook_id}
                                                person={p}
                                                expanded={!!expandedPersons[p.pitchbook_id]}
                                                onToggleExpand={() => setExpandedPersons((s) => ({ ...s, [p.pitchbook_id]: !s[p.pitchbook_id] }))}
                                                highlighted={p.pitchbook_id === personFocusId}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-stone-200">
                    <MovedToListManagement compact />
                    <Button variant="ghost" onClick={onClose}>Close</Button>
                </div>
            </div>
        </div>
    ), document.body);
}
